// Update completion progress bar client-side when a user fills in an answer
// This provides immediate visual feedback before the server-side calculation
// Uses question-group weighting to match server-side calculation:
//   - Each question group contributes equally (100 / num_groups)
//   - Within a group, each item contributes (group_weight / items_in_group)

// Cache for question group item counts to avoid repeated DOM traversal
let groupItemCountsCache = null;
let cachedTotalGroups = null;
let cachedVisibleGroupCount = null;

// Global map to track filled state and applied weight
// Each entry: { filled: boolean, appliedWeight: number }
// Survives element destruction by typeahead
const progressTrackingState = new Map();
const RECENT_PROGRESS_CACHE_MS = 5 * 60 * 1000;

// Set to true once the user has changed an input value.
// Prevents the initial DOM scan on page load from computing a stale-low
// percent (e.g. from partially-initialized typeaheads) and reducing the bar
// below what the server already confirmed.
let hasUserInteracted = false;

// Set to true once a live poll response has been processed.
// Prevents the initial server-rendered percent (which may come from a stale
// tracking record) from displaying 100% before the first poll confirms it.
// Cleared on each page load so a hard reload / new tab always goes through this gate.
let hasReceivedPollUpdate = false;
let lastWrittenPercent = null;

export function markPollUpdateReceived() {
  hasReceivedPollUpdate = true;
}

if (typeof window !== 'undefined') {
  window.markSurveyPollUpdateReceived = markPollUpdateReceived;
}

function getProgressCacheKey() {
  const pollIndicator = $('.polling-indicator');
  if (pollIndicator.length === 0) { return null; }

  const surveyId = pollIndicator.attr('data-survey-id');
  if (!surveyId) { return null; }

  return `survey-progress-percent-${surveyId}`;
}

function readCachedProgressPercent() {
  // Prefer localStorage so the cache survives across new tabs and hard reloads.
  // This prevents the bar from flashing to 0 on a new tab when the server
  // renders 0% (e.g. nil tracking record) before the first poll completes.
  const storage = (typeof window !== 'undefined') && (window.localStorage || window.sessionStorage);
  if (!storage) { return null; }

  const cacheKey = getProgressCacheKey();
  if (!cacheKey) { return null; }

  let rawValue;
  try {
    rawValue = storage.getItem(cacheKey);
  } catch (e) {
    return null;
  }
  if (rawValue === null) { return null; }

  try {
    const parsedPayload = JSON.parse(rawValue);
    if (parsedPayload && typeof parsedPayload === 'object') {
      const parsedPercent = parseFloat(parsedPayload.percent);
      const parsedUpdatedAt = parseInt(parsedPayload.updatedAt, 10);
      if (Number.isNaN(parsedPercent)) { return null; }

      return {
        percent: Math.max(0, Math.min(100, parsedPercent)),
        updatedAt: Number.isNaN(parsedUpdatedAt) ? 0 : parsedUpdatedAt
      };
    }
  } catch (error) {
    // Backward compatibility with older plain-number cache values.
  }

  const parsedValue = parseFloat(rawValue);
  if (Number.isNaN(parsedValue)) { return null; }

  return {
    percent: Math.max(0, Math.min(100, parsedValue)),
    updatedAt: 0
  };
}

function writeCachedProgressPercent(percent) {
  const storage = (typeof window !== 'undefined') && (window.localStorage || window.sessionStorage);
  if (!storage) { return; }

  const cacheKey = getProgressCacheKey();
  if (!cacheKey) { return; }

  try {
    storage.setItem(cacheKey, JSON.stringify({
      percent: Math.max(0, Math.min(100, percent)),
      updatedAt: Date.now()
    }));
  } catch (e) {
    // Quota exceeded or storage blocked; treat as no-op
  }
}

if (typeof window !== 'undefined') {
  window.writeCachedSurveyProgressPercent = writeCachedProgressPercent;
}

export function syncSurveySubmitState() {
  const tracker = $('.data-collection-tracker');
  if (tracker.length === 0 || typeof tracker.attr('data-server-percent') === 'undefined') {
    return;
  }

  const minimumCompletion = parseFloat(tracker.attr('data-minimum-completion') || '0.9');
  const surveyStatus = $('.polling-indicator').attr('data-status') || $('#hidden_status').val();
  const isSubmitted = surveyStatus === 'submitted';
  const isApproved = surveyStatus === 'approved';
  const hasErrors = $('.question-row.border-error').length > 0;

  // Calculate current progress: server baseline + client offset
  const serverPercent = parseFloat(tracker.attr('data-server-percent') || '0');
  const clientOffset = parseFloat(tracker.data('client-offset') || '0');
  const currentPercent = serverPercent + clientOffset;
  const clampedPercent = Math.max(0, Math.min(100, currentPercent));

  // Check if meets minimum completion (convert threshold to percentage)
  const hasMinCompleted = clampedPercent >= (minimumCompletion * 100);
  const canSubmit = !isSubmitted && !isApproved && hasMinCompleted && !hasErrors;

  // Get button elements
  const submitButtons = $('.submit-survey-button');
  const thresholdButton = submitButtons.filter('.threshold-not-reached');
  const submittedButton = submitButtons.filter('.submitted').not('.approved');
  const approvedButton = submitButtons.filter('.approved');
  const activeSubmitButton = submitButtons.not('.threshold-not-reached').not('.submitted').not('.approved');

  // Hide all submit buttons initially
  submitButtons.addClass('d-none');

  // Show appropriate button based on state
  if (isApproved) {
    approvedButton.removeClass('d-none');
    return;
  }

  if (isSubmitted) {
    submittedButton.removeClass('d-none');
    return;
  }

  // Build tooltip message for threshold button
  const tooltipMessages = [];
  if (!hasMinCompleted) {
    tooltipMessages.push(`Please complete at least ${Math.round(minimumCompletion * 100)}% of the survey before submitting.`);
  }
  if (hasErrors) {
    tooltipMessages.push('Please fix any issues in the survey before submitting.');
  }

  if (canSubmit) {
    activeSubmitButton.removeClass('d-none');
    thresholdButton.addClass('d-none');
  } else {
    thresholdButton.removeClass('d-none');
    thresholdButton.attr('data-original-title', tooltipMessages.join(' '));
  }
}

if (typeof window !== 'undefined') {
  window.syncSurveySubmitState = syncSurveySubmitState;
}

// Initialize tracking state for all answer inputs on page load
// This must happen BEFORE any user interaction so we can detect state changes
export function initializeProgressTracking() {
  // Initialize all answer inputs with their current filled state BEFORE user interaction
  const tracker = $('.data-collection-tracker');
  if (tracker.length === 0) return;

  // Reset per-page flags so Turbo navigation between surveys starts clean.
  // Without this, hasReceivedPollUpdate=true from the previous survey would
  // allow 100% display before the first poll confirms the new survey's state,
  // and hasUserInteracted=true would skip the initial-load bar-floor protection.
  hasUserInteracted = false;
  hasReceivedPollUpdate = false;
  lastWrittenPercent = null;

  // Clear stale tracking state from any previous survey visited in this tab.
  // Without this, inputs sharing the same tracking key across surveys (e.g. same
  // question/answer IDs) would inherit wrong filled/appliedWeight values, causing
  // incorrect clientOffset deltas on the new survey.
  progressTrackingState.clear();

  // Pre-calculate group item counts for performance
  groupItemCountsCache = calculateGroupItemCounts();
  cachedTotalGroups = parseInt(tracker.attr('data-total-groups') || '0', 10);
  cachedVisibleGroupCount = $('.card[data-question-group-id]:not(.d-none)').length;
  
  // Skip .tt-hint elements (typeahead creates these as read-only hint clones)
  const answerTexts = $('.answer-text:not(.tt-hint)');
  
  answerTexts.each(function() {
    const $input = $(this);
    const trackingKey = getTrackingKey($input);
    if (!trackingKey) return;
    
    const isFilled = ($input.val() || '').toString().trim().length > 0;
    
    // Only set if not already tracked (don't overwrite existing state)
    if (!progressTrackingState.has(trackingKey)) {
      progressTrackingState.set(trackingKey, { filled: isFilled, appliedWeight: 0 });
    }
  });
}

// Generate unique tracking key for an input element
// Uses id first, then name + position for uniqueness
function getTrackingKey($input) {
  const inputId = $input.attr('id');
  if (inputId) {
    return 'progress-' + inputId;
  }
  
  const inputName = $input.attr('name');
  if (!inputName) return null;
  
  // For inputs without id, use name + position within closest container
  // This handles table cells and other grouped inputs
  const position = $input.closest('.input-group, .survey-table-cell, td').find('.answer-text').index($input);
  return 'progress-' + inputName + '-' + position;
}

// Calculate required item counts per question group (only visible/displayed groups)
function calculateGroupItemCounts() {
  const counts = {};
  
  // Only count visible cards (not hidden by conditionals via d-none)
  $('.card[data-question-group-id]:not(.d-none)').each(function() {
    const $card = $(this);
    const groupId = $card.data('question-group-id');
    
    // Only count visible/displayed question rows (not hidden by conditionals)
    // Count required questions (non-table question rows with required=true)
    const requiredQuestions = $card.find('.question-row[data-question-id][data-required="true"]:not(.d-none)').length;
    
    // Count tables (each table counts as 1 item, regardless of cells)
    // Tables have data-table-id instead of data-question-id
    const tables = $card.find('.question-row[data-table-id]:not(.d-none)').length;
    
    counts[groupId] = requiredQuestions + tables;
  });
  
  return counts;
}

function normalizeInputValue($input) {
  if (!$input || $input.length === 0) return '';
  return ($input.val() || '').toString().trim();
}

function isQuestionRowFilled($row) {
  if (!$row || $row.length === 0 || $row.hasClass('d-none')) {
    return false;
  }

  const answerInputs = $row.find('.answer-text:not(:disabled):not(.tt-hint)');
  let hasValue = false;
  answerInputs.each(function() {
    if (normalizeInputValue($(this)).length > 0) {
      hasValue = true;
      return false;
    }
    return true;
  });

  if (hasValue) {
    return true;
  }

  // Treat explicit not-applicable / prefer-not-disclose as answered.
  return $row.find('.mark-not-applicable:checked, .prefer-not-disclose:checked').length > 0;
}

function isTableRowFilled($row) {
  if (!$row || $row.length === 0 || $row.hasClass('d-none')) {
    return false;
  }

  const tableInputs = $row.find('.survey-table-cell .answer-text:not(:disabled):not(.tt-hint)');
  let hasValue = false;
  tableInputs.each(function() {
    if (normalizeInputValue($(this)).length > 0) {
      hasValue = true;
      return false;
    }
    return true;
  });

  return hasValue;
}

function computeOptimisticPercent(totalGroups) {
  const visibleCards = $('.card[data-question-group-id]:not(.d-none)');
  const visibleGroupCount = visibleCards.length;
  const effectiveGroupCount = visibleGroupCount > 0 ? visibleGroupCount : totalGroups;

  if (effectiveGroupCount <= 0) {
    return 100;
  }

  let completionPercent = 0;
  let measurableItemsCount = 0;
  let hasPartiallyRenderedGroups = false;

  visibleCards.each(function() {
    const $card = $(this);
    const requiredQuestionRows = $card.find('.question-row[data-question-id][data-required="true"]:not(.d-none)');
    const tableRows = $card.find('.question-row[data-table-id]:not(.d-none)');
    const totalItems = requiredQuestionRows.length + tableRows.length;

    if (totalItems <= 0) {
      // Distinguish between two causes of zero required items:
      //
      // 1. Collapsed / not-yet-loaded group: the .collapse list is empty,
      //    so $card.find('.question-row') returns nothing.  We can't know
      //    the answered state → bail out to avoid a false optimistic value.
      //
      // 2. Content-only group: the accordion IS expanded but only has
      //    .question-row[data-content-id] rows (instructions, methodology,
      //    etc.) with no required questions or tables.  There is nothing for
      //    the user to answer, so the group is automatically 100% complete.
      //    Treat it that way rather than aborting the whole computation.
      const hasAnyRows = $card.find('.question-row').length > 0;
      if (hasAnyRows) {
        // Content/optional-only group: auto-complete, add its full weight.
        completionPercent += (100 / effectiveGroupCount);
        measurableItemsCount += 1;
      } else {
        // Collapsed or unloaded group: state unknown, skip reconciliation.
        hasPartiallyRenderedGroups = true;
        return false;
      }
      return true;
    }

    measurableItemsCount += totalItems;

    const itemWeight = (100 / effectiveGroupCount) / totalItems;

    requiredQuestionRows.each(function() {
      if (isQuestionRowFilled($(this))) {
        completionPercent += itemWeight;
      }
    });

    tableRows.each(function() {
      if (isTableRowFilled($(this))) {
        completionPercent += itemWeight;
      }
    });

    return true;
  });

  // When groups are collapsed, question/table rows may be intentionally removed
  // from the DOM; in that case, skip optimistic reconciliation rather than
  // computing against a partial survey snapshot.
  if (measurableItemsCount === 0 || hasPartiallyRenderedGroups) {
    return null;
  }

  return Math.max(0, Math.min(100, completionPercent));
}

// Get the increment value for an item based on its question group
function getIncrementForItem($input, totalGroups) {
  if (totalGroups <= 0) return 0;
  
  // Count currently visible question groups to detect conditional changes
  const visibleGroupCount = $('.card[data-question-group-id]:not(.d-none)').length;
  
  // Invalidate cache if server totalGroups changed OR if visible group count changed from cached value
  if (cachedTotalGroups !== totalGroups || cachedVisibleGroupCount !== visibleGroupCount) {
    groupItemCountsCache = null;
    cachedTotalGroups = totalGroups;
    cachedVisibleGroupCount = visibleGroupCount;
  }
  
  // Ensure cache is populated
  if (!groupItemCountsCache) {
    groupItemCountsCache = calculateGroupItemCounts();
  }
  
  const $card = $input.closest('.card[data-question-group-id]');
  if ($card.length === 0) return 0;
  
  // If this card is hidden, don't count it
  if ($card.hasClass('d-none')) return 0;
  
  const groupId = $card.data('question-group-id');
  const itemsInGroup = groupItemCountsCache[groupId] || 0;
  
  if (itemsInGroup <= 0) return 0;
  
  // Use actual visible group count for calculation to match server logic
  const effectiveGroupCount = visibleGroupCount > 0 ? visibleGroupCount : totalGroups;
  
  // Server formula: group_weight = 1/num_groups, item_weight = group_weight / items_in_group
  // In percentage terms: (100 / num_groups) / items_in_group
  return (100 / effectiveGroupCount) / itemsInGroup;
}

export default function(inputElement) {
  const tracker = $('.data-collection-tracker');
  if (tracker.length === 0) {
    return;
  }

  // Check if we're using sections display mode (not a progress bar)
  const completionDisplay = tracker.find('h5.font-weight-bold').length > 0;
  if (completionDisplay) {
    return;
  }

  const progressBar = tracker.find('.Progress .progress2');
  const percentLabel = tracker.find('.completion-percent');
  if (progressBar.length === 0 && percentLabel.length === 0) {
    return;
  }

  // Get server values
  const serverPercent = parseFloat(tracker.attr('data-server-percent') || '0');
  const totalGroups = parseInt(tracker.attr('data-total-groups') || '0', 10);
  
  // Get current client-side offset from server percentage
  let clientOffset = parseFloat(tracker.data('client-offset') || '0');

  // Track changes if input element provided
  if (inputElement && totalGroups > 0) {
    hasUserInteracted = true;
    let $input = $(inputElement);
    
    // If this is a typeahead hint element, find the real input instead
    if ($input.hasClass('tt-hint')) {
      const $realInput = $input.closest('.twitter-typeahead').find('.tt-input.answer-text');
      if ($realInput.length > 0) {
        $input = $realInput;
      } else {
        // Can't find real input, skip tracking
        return;
      }
    }
    
    const currentValue = ($input.val() || '').toString().trim();
    const isFilled = currentValue.length > 0;
    
    // Use unique tracking key for this input
    const trackingKey = getTrackingKey($input);
    if (!trackingKey) return;
    
    // Get previous state from global map. If missing (e.g. recreated DOM node),
    // infer baseline from the element's initial HTML value so first observed change
    // is still treated as an explicit add or subtract.
    if (!progressTrackingState.has(trackingKey)) {
      const initialValue = ($input.attr('value') || '').toString().trim();
      const initiallyFilled = initialValue.length > 0;

      let initialAppliedWeight = 0;
      if (initiallyFilled) {
        const initialQuestionRow = $input.closest('.question-row');
        const initialTableId = initialQuestionRow.data('table-id');
        const initialReqVal = initialQuestionRow.data('required');
        const initiallyRequired = initialReqVal === true || initialReqVal === 'true';

        // Required non-table rows and table rows contribute to completion.
        if (initialTableId !== undefined || initiallyRequired) {
          initialAppliedWeight = getIncrementForItem($input, totalGroups);
        }
      }

      progressTrackingState.set(trackingKey, { filled: initiallyFilled, appliedWeight: initialAppliedWeight });
    }

    const previousState = progressTrackingState.get(trackingKey) || { filled: false, appliedWeight: 0 };
    const wasFilled = previousState.filled;
    const previousAppliedWeight = previousState.appliedWeight;

    // Only act if state changed
    if (isFilled && !wasFilled) {
        // Check if this is a required question or table
        const questionRow = $input.closest('.question-row');
        const tableId = questionRow.data('table-id');
        const reqVal = questionRow.data('required');
        const isRequired = reqVal === true || reqVal === 'true';
        
        if (tableId !== undefined) {
          // Table - only increment if this is the first filled cell
          const tableHasOtherFilledCells = questionRow.find('.answer-text').not($input).filter(function() {
            return ($(this).val() || '').toString().trim().length > 0;
          }).length > 0;
          
          if (!tableHasOtherFilledCells) {
            const increment = getIncrementForItem($input, totalGroups);
            clientOffset += increment;
            tracker.data('client-offset', clientOffset);
            progressTrackingState.set(trackingKey, { filled: true, appliedWeight: increment });
          }
        } else if (isRequired) {
          const increment = getIncrementForItem($input, totalGroups);
          clientOffset += increment;
          tracker.data('client-offset', clientOffset);
          progressTrackingState.set(trackingKey, { filled: true, appliedWeight: increment });
        }
    } else if (!isFilled && wasFilled) {
        // Clearing a field - subtract the STORED weight, or calculate weight for pre-filled fields
        const questionRow = $input.closest('.question-row');
        const tableId = questionRow.data('table-id');
        const reqVal = questionRow.data('required');
        const isRequired = reqVal === true || reqVal === 'true';
        
        if (tableId !== undefined) {
          // Table - only decrement if this was the last filled cell
          const tableHasOtherFilledCells = questionRow.find('.answer-text').not($input).filter(function() {
            return ($(this).val() || '').toString().trim().length > 0;
          }).length > 0;
          
          if (!tableHasOtherFilledCells) {
            // For pre-filled table cells, calculate weight; otherwise use stored weight
            let weightToSubtract = previousAppliedWeight;
            if (weightToSubtract === 0 && wasFilled) {
              // Pre-filled table cell - calculate what it contributed
              weightToSubtract = getIncrementForItem($input, totalGroups);
            }
            clientOffset -= weightToSubtract;
            tracker.data('client-offset', clientOffset);
            progressTrackingState.set(trackingKey, { filled: false, appliedWeight: 0 });
          }
        } else if (isRequired) {
          // Non-table field - only required fields contribute to progress
          // For pre-filled required fields, calculate weight; otherwise use stored weight
          let weightToSubtract = previousAppliedWeight;
          if (weightToSubtract === 0 && wasFilled) {
            // Pre-filled required field - calculate what it contributed
            weightToSubtract = getIncrementForItem($input, totalGroups);
          }
          clientOffset -= weightToSubtract;
          tracker.data('client-offset', clientOffset);
          progressTrackingState.set(trackingKey, { filled: false, appliedWeight: 0 });
        }
    }
  }

  // Reconcile with current visible DOM so conditional reveal/hide immediately rebalances
  // progress and does not wait for poll/worker completion.
  const optimisticPercent = computeOptimisticPercent(totalGroups);
  if (optimisticPercent !== null) {
    const candidateOffset = optimisticPercent - serverPercent;
    // On initial page load (before any user interaction), don't let the DOM scan
    // push the bar below what the server already confirmed.  This prevents a
    // flash-to-zero caused by typeaheads or other inputs that haven't settled yet.
    clientOffset = (!hasUserInteracted && candidateOffset < 0) ? 0 : candidateOffset;
    tracker.data('client-offset', clientOffset);
  } else if (!hasUserInteracted) {
    // Only use the cache before the user has interacted.  Once the incremental
    // tracker has started updating clientOffset, the cache value is stale and
    // must not overwrite the live tracking state.  Without this guard, the
    // initial UpdateProgressBar() call writes 0% to the cache, and the cache
    // then resets clientOffset back to 0 on every subsequent null-optimistic call
    // (i.e. while collapsed groups keep computeOptimisticPercent returning null),
    // causing the bar to stay frozen at 0% for template surveys.
    const cachedProgress = readCachedProgressPercent();
    const isRecentCache = cachedProgress && cachedProgress.updatedAt > 0 && (Date.now() - cachedProgress.updatedAt) <= RECENT_PROGRESS_CACHE_MS;
    if (cachedProgress !== null && (isRecentCache || cachedProgress.percent > serverPercent)) {
      const candidateOffset = cachedProgress.percent - serverPercent;
      // Only apply cache when it keeps the bar at or above the server-confirmed value.
      // A negative candidateOffset means the server has moved ahead of the cache (e.g.
      // after an import completes), and applying it would incorrectly pull the bar down.
      if (candidateOffset >= 0) {
        clientOffset = candidateOffset;
        tracker.data('client-offset', clientOffset);
      }
    }
  }

  // Calculate display percentage: server baseline + reconciled client offset
  const currentPercent = serverPercent + clientOffset;
  
  // Round and clamp to 0-100
  // Cap at 99% until server confirms 100%
  let percent = Math.round(currentPercent);
  // On initial page load the server percent comes from the tracking record which
  // may be stale (e.g. answers cleared but tracking not yet recalculated).
  // Only allow 100% once a live poll response has confirmed the current state.
  const surveyStatusForCap = $('.polling-indicator').attr('data-status');
  const isSurveyFinalized = surveyStatusForCap === 'submitted' || surveyStatusForCap === 'approved';
  if (percent >= 100 && (!hasReceivedPollUpdate || serverPercent < 100) && !isSurveyFinalized) {
    percent = 99;
  }
  percent = Math.max(0, Math.min(100, percent));

  // Update display
  if (progressBar.length > 0) {
    progressBar.css('width', percent + '%');
    const bgColor = getProgressColor(percent);
    progressBar.css('background', bgColor);
  }
  if (percentLabel.length > 0) {
    percentLabel.text(percent + '%');
  }

  if (percent !== lastWrittenPercent) {
    lastWrittenPercent = percent;
    writeCachedProgressPercent(percent);
  }

  // Sync submit button state immediately when progress changes
  syncSurveySubmitState();
}

function getProgressColor(percent) {
  if (percent >= 75) return '#076E24';
  if (percent >= 60) return '#38CD63';
  if (percent >= 40) return '#DB9401';
  if (percent >= 20) return '#E67C85';
  return '#DA3547';
}
